import React, { useState, useEffect } from 'react';
import { Container, Button, Form, Image, Spinner } from 'react-bootstrap';
import { useNavigate, useParams } from 'react-router-dom';
import { toast } from 'react-toastify';
import api from '../services/api';
import {
  getWithdrawalErrorMessage,
  getWithdrawalMethod,
} from './WithdrawDetails.utils';

const WithdrawDetails = () => {
  const { coin } = useParams();
  const navigate = useNavigate();
  const [image, setImage] = useState(null);
  const [qrFile, setQrFile] = useState(null);
  const [withdrawalAddress, setWithdrawalAddress] = useState('');
  const [amount, setAmount] = useState('');
  const [transactionPassword, setTransactionPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);
  const [submitting, setSubmitting] = useState(false);
  const [network] = useState('TRC20');
  const [availableBalance, setAvailableBalance] = useState(0);
  const [loadingBalance, setLoadingBalance] = useState(true);

  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth <= 768);
    };
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  useEffect(() => {
    const fetchBalance = async () => {
      try {
        setLoadingBalance(true);
        const response = await api.get('/api/trading/wallet/balances');

        if (response.data?.success && response.data?.data?.real) {
          const coinBalance = response.data.data.real[coin.toUpperCase()];
          if (coinBalance) {
            setAvailableBalance(coinBalance.availableAssets || 0);
          }
        }
      } catch (error) {
      } finally {
        setLoadingBalance(false);
      }
    };

    if (coin) {
      fetchBalance();
    }
  }, [coin]);

  const handleUpload = (e) => {
    const file = e.target.files[0];
    if (file) {
      setQrFile(file);
      setImage(URL.createObjectURL(file));
    }
  };

  const handleSubmit = async () => {
    if (!withdrawalAddress.trim()) {
      toast.error('Please enter withdrawal address', {
        position: 'bottom-left',
        autoClose: 3000,
      });
      return;
    }
    if (!amount.trim() || parseFloat(amount) <= 0) {
      toast.error('Please enter valid withdrawal amount', {
        position: 'bottom-left',
        autoClose: 3000,
      });
      return;
    }
    if (!transactionPassword.trim()) {
      toast.error('Please enter your fund password', {
        position: 'bottom-left',
        autoClose: 3000,
      });
      return;
    }

    try {
      setSubmitting(true);
      const formData = new FormData();
      formData.append('currencyCode', coin.toUpperCase());
      formData.append('amount', parseFloat(amount));
      formData.append('method', getWithdrawalMethod(coin, network));
      formData.append('destination', withdrawalAddress.trim());
      formData.append('transactionPassword', transactionPassword.trim());

      // Add QR code file if uploaded
      if (qrFile) {
        formData.append('qrCode', qrFile);
      }

      // Submit withdrawal request
      const response = await api.post('/api/financial/withdrawal', formData);

      if (response.data?.success) {
        toast.success(
          response.data.message || 'Withdrawal request submitted successfully!',
          {
            position: 'bottom-left',
            autoClose: 3000,
          }
        );

        // Clear form
        setWithdrawalAddress('');
        setAmount('');
        setImage(null);
        setQrFile(null);

        // Navigate to withdrawal records after a short delay
        setTimeout(() => {
          navigate('/withdrawal-records');
        }, 1500);
      } else {
        toast.error(response.data?.message || 'Failed to submit withdrawal', {
          position: 'bottom-left',
          autoClose: 3000,
        });
      }
    } catch (error) {
      const errorMessage = getWithdrawalErrorMessage(error);

      toast.error(errorMessage, {
        position: 'bottom-left',
        autoClose: 5000,
      });
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Container
      fluid
      style={{
        maxWidth: isMobile ? '100%' : '85%',
        padding: 'clamp(1rem, 3vw, 1.5rem)',
        backgroundColor: '#FFFFFF',
        minHeight: '100vh',
      }}
    >
      {/* Header */}
      <div className='d-flex justify-content-between align-items-center mb-4'>
        <Button
          variant='link'
          onClick={() => navigate(-1)}
          className='text-dark p-0 me-2'
          aria-label='Go back'
        >
          <i className='bi bi-arrow-left fs-5'></i>
        </Button>
        <h5 className='mb-0 fw-bold'>Withdraw</h5>
        <Button
          variant='link'
          onClick={() => navigate('/withdrawal-records')}
          className='text-dark p-0'
          aria-label='Withdrawal records'
        >
          <i className='bi bi-clock fs-5'></i>
        </Button>
      </div>

      {/* Main Content */}
      <div style={{ paddingBottom: '100px' }}>
        {/* Coin Info */}
        <div className='bg-light rounded shadow-sm p-3 mb-3'>
          <div className='d-flex justify-content-between align-items-center'>
            <strong className='text-dark'>{coin}</strong>
            <span
              className='text-primary'
              style={{ cursor: 'pointer', fontSize: '0.9rem' }}
              onClick={() => navigate('/withdraw')}
            >
              Select a currency
            </span>
          </div>
        </div>

        {/* Network */}
        <div className='d-flex align-items-center mb-3'>
          <strong className='me-2'>Withdrawal Network</strong>
          <img
            src={require(`../assets/coins/${coin.toLowerCase()}.png`)}
            alt={coin}
            width='20'
            height='20'
            className='me-1'
          />
          <span>{coin.toUpperCase()}</span>
        </div>

        {/* Upload QR */}
        <Form.Group className='mb-3'>
          <Form.Label className='fw-semibold text-dark'>
            <strong>Upload the two-dimensional code</strong>
          </Form.Label>
          <Form.Control
            type='file'
            accept='image/*'
            style={{ display: 'none' }}
            id='upload-qr'
            onChange={handleUpload}
          />
          <div
            className='text-white text-center rounded p-5 d-flex flex-column align-items-center justify-content-center'
            style={{
              backgroundColor: '#667eea',
              cursor: 'pointer',
              minHeight: '150px',
              transition: 'all 0.3s ease',
            }}
            onClick={() => document.getElementById('upload-qr').click()}
          >
            {image ? (
              <Image
                src={image}
                alt='Uploaded'
                thumbnail
                style={{ maxWidth: '100px' }}
              />
            ) : (
              <>
                <div style={{ fontSize: '24px' }}>+</div>
                <div>Click Upload</div>
              </>
            )}
          </div>
        </Form.Group>

        {/* Withdrawal Info */}
        <Form.Group className='mb-3'>
          <Form.Label className='fw-semibold text-dark'>
            Withdraw address{' '}
            <span className='text-primary' style={{ cursor: 'pointer' }}>
              Withdrawal Details
            </span>
          </Form.Label>
          <div className='d-flex gap-2'>
            <Form.Control
              type='text'
              placeholder='Enter Withdrawal Address'
              value={withdrawalAddress}
              onChange={(e) => setWithdrawalAddress(e.target.value)}
              disabled={submitting}
              style={{
                border: '2px solid #e2e8f0',
                borderRadius: '10px',
                padding: '0.75rem 1rem',
              }}
            />
            <Button
              className='px-3'
              style={{
                background: 'linear-gradient(135deg, #00C851, #007E33)',
                border: 'none',
                borderRadius: '10px',
                whiteSpace: 'nowrap',
              }}
              disabled={submitting}
              onClick={() => {
                if (withdrawalAddress.trim()) {
                  navigator.clipboard.writeText(withdrawalAddress.trim());
                  toast.success('Address copied to clipboard!', {
                    position: 'bottom-left',
                    autoClose: 2000,
                  });
                } else {
                  toast.error('Please enter an address first', {
                    position: 'bottom-left',
                    autoClose: 2000,
                  });
                }
              }}
            >
              Clone
            </Button>
          </div>
        </Form.Group>

        <Form.Group className='mb-3'>
          <Form.Label className='fw-semibold text-dark'>
            Number of withdrawals
          </Form.Label>
          <Form.Control
            type='number'
            placeholder='Please enter the withdrawal amount'
            value={amount}
            onChange={(e) => setAmount(e.target.value)}
            min='0'
            step='any'
            disabled={submitting}
            style={{
              border: '2px solid #e2e8f0',
              borderRadius: '10px',
              padding: '0.75rem 1rem',
            }}
          />
          <div className='text-muted small mt-2'>
            Available:{' '}
            {loadingBalance ? (
              <Spinner animation='border' size='sm' />
            ) : (
              `${availableBalance.toFixed(2)} ${coin.toUpperCase()}`
            )}
          </div>
        </Form.Group>

        {/* Fund Password Field */}
        <Form.Group className='mb-3'>
          <Form.Label className='fw-semibold text-dark'>
            Fund Password
          </Form.Label>
          <div className='position-relative'>
            <Form.Control
              type={showPassword ? 'text' : 'password'}
              placeholder='Please enter your fund password'
              value={transactionPassword}
              onChange={(e) => setTransactionPassword(e.target.value)}
              disabled={submitting}
              style={{
                border: '2px solid #e2e8f0',
                borderRadius: '10px',
                padding: '0.75rem 1rem',
                paddingRight: '3rem',
              }}
            />
            <Button
              variant='link'
              className='position-absolute top-50 end-0 translate-middle-y'
              onClick={() => setShowPassword(!showPassword)}
              disabled={submitting}
              style={{
                border: 'none',
                background: 'transparent',
                color: '#6c757d',
                padding: '0.5rem 1rem',
              }}
            >
              <i
                className={`bi ${showPassword ? 'bi-eye-slash' : 'bi-eye'}`}
                style={{ fontSize: '1.2rem' }}
              ></i>
            </Button>
          </div>
        </Form.Group>

        <div className='text-muted small mb-3 p-3 bg-light rounded'>
          <div className='d-flex justify-content-between mb-1'>
            <span>Handling fee</span>
            <span className='fw-semibold'>0.00028 {coin}</span>
          </div>
          <div className='d-flex justify-content-between'>
            <span>Actual arrival</span>
            <span className='fw-semibold'>0.00000 {coin}</span>
          </div>
        </div>

        <Button
          className='w-100 mb-4 py-3 fw-semibold'
          style={{
            background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
            border: 'none',
            borderRadius: '12px',
            boxShadow: '0 4px 15px rgba(102, 126, 234, 0.4)',
            transition: 'all 0.3s ease',
          }}
          onClick={handleSubmit}
          disabled={submitting}
        >
          {submitting ? (
            <>
              <Spinner
                as='span'
                animation='border'
                size='sm'
                role='status'
                aria-hidden='true'
                className='me-2'
              />
              Processing...
            </>
          ) : (
            'Submit'
          )}
        </Button>

        <div className='small text-muted p-3 bg-light rounded'>
          <p className='fw-bold text-dark mb-2'>Tips</p>
          <p className='mb-2'>1. Minimum withdrawal amount is: 0.0005 {coin}</p>
          <p className='mb-2'>
            2. To ensure the security of funds, your current account security
            policy, password change, use a new
          </p>
          <p className='mb-0'>
            3. Make sure your computer and browser are secure to prevent
            information from being tampered with or leaked.
          </p>
        </div>
      </div>
    </Container>
  );
};

export default WithdrawDetails;
